import type { ReactNode } from 'react';
import { clsx } from '../../../../ds/clsx';
import { Banner } from '../../../../ds/components';
import { useIsMobile } from '../../../../ds/hooks';
import { ApiError, errorMessage } from '../../../../lib/api';
import styles from './OrganizationPage.module.css';

/**
 * Page chrome of the organization settings built in M8 by F3: Admin 01–06 (`variant="admin"`, the Admin
 * file's header with the navy „Admin" pill, rendered inside the settings frame — D-70 pattern) and
 * Einstellungen 25–28 (`variant="settings"`, the navy „Admin" tag). Organization settings are „nur am
 * Rechner" (Einstellungen M1): below 768 px the page shows that note instead of its content.
 */
export interface OrganizationPageProps {
  title: string;
  description?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  variant?: 'admin' | 'settings' | undefined;
  children: ReactNode;
}

export function OrganizationPage({
  title,
  description,
  actions,
  variant = 'settings',
  children,
}: OrganizationPageProps) {
  const mobile = useIsMobile();
  const admin = variant === 'admin';
  if (mobile) {
    return (
      <div className={styles.desktopOnly}>
        <span className={styles.desktopOnlyTitle}>{title}</span>
        <span className={styles.desktopOnlyText}>Nur am Rechner bearbeitbar</span>
      </div>
    );
  }
  return (
    <div className={clsx(styles.page, admin && styles.pageAdmin)}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <span className={styles.eyebrowRow}>
            <span className={styles.eyebrow}>Organisation</span>
            <span className={admin ? styles.adminPill : styles.adminTag}>Admin</span>
          </span>
          <h2 className={clsx(styles.title, admin && styles.titleAdmin)}>{title}</h2>
          {description ? (
            <p className={clsx(styles.description, admin && styles.descriptionAdmin)}>{description}</p>
          ) : null}
        </div>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

/** Error of a page query: 403 → the locked note with the server's text, anything else → the error banner. */
export function QueryError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.status === 403) return <Banner tone="info">{error.message}</Banner>;
  return <Banner tone="danger">{errorMessage(error)}</Banner>;
}

export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}
